import React, { useEffect, useState } from 'react';

import { discountTypes } from 'constants.js';
import { useFormikContext } from 'formik';
import { useRecoilValue } from 'recoil';

import { twoDecimalPlaces } from 'components/Payments/components/Invoices/invoicesHelpers';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import { permissions } from 'components/state';

import { calcInvoiceDiscountFromProducts, getProductDiscountFromInvoice } from './helpers';

const MAX_PERCENT = 10000;

const Discount = ({ id, type }) => {
  const permission = useRecoilValue(permissions);

  const { initialValues, values, setFieldValue } = useFormikContext();

  const [discountState, setDiscountState] = useState();

  const currentItem = values?.[type]?.find((item) => item.id === id) || {};
  const hasDiscountInProducts = !!initialValues?.[type].find((item) => item.id === id)?.discount;

  const discount = currentItem?.discount || discountState;
  const amount =
    type === 'products'
      ? currentItem?.total_amount_cents
      : (currentItem?.pt_balance ?? currentItem?.total_cost_cents);

  useEffect(() => {
    // update discount of old versions that had discount only in invoice level
    if (!discount && !hasDiscountInProducts && initialValues?.invoice?.discount) {
      setDiscountState(getProductDiscountFromInvoice({ ...initialValues, productCost: amount }));
    }
  }, [discount, amount, hasDiscountInProducts, initialValues?.invoice?.discount]);

  const setDiscount = (discountObj) => {
    const updatedItems = values?.[type]?.map((item) =>
      item.id === id ? { ...item, discount: discountObj } : item
    );

    setFieldValue(type, updatedItems);

    const invoiceDiscount = calcInvoiceDiscountFromProducts({
      ...(values || {}),
      initialInvoice: initialValues?.invoice,
      [type]: updatedItems,
      hasDiscountInProducts
    });

    setFieldValue('invoice.discount', invoiceDiscount);
  };

  const handleDiscountChange = (newBalance) => {
    const discountObj = {
      amount_cents: discount?.amount_cents || 0,
      type: discount?.type || 'amount',
      percentage: discount?.percentage || 0
    };

    if (discountObj?.type === 'percent') {
      discountObj.percentage = Math.min(newBalance, MAX_PERCENT);
      discountObj.amount_cents = Math.round((discountObj.percentage / 10000) * amount);
    } else {
      discountObj.amount_cents = Math.min(newBalance, amount);
      discountObj.percentage = Math.round(
        twoDecimalPlaces((discountObj.amount_cents / amount) * 10000)
      );
    }

    setDiscount(discountObj);
  };

  const handleUnitChange = (discountType) => {
    const discountObj = {
      amount_cents: discount?.amount_cents || 0,
      type: discountType || discount?.type || 'amount',
      percentage: discount?.percentage || 0
    };

    setDiscount(discountObj);
  };

  const isPercent = discount?.type === 'percent';

  const newPercentageValue = +(discount?.amount_cents / amount) * 10000;
  const newAmountValue = +(discount?.percentage / 10000) * amount;

  const newDiscountValue = isPercent
    ? discount?.percentage || newPercentageValue || 0
    : discount?.amount_cents || newAmountValue || 0;

  return (
    <CurrencyInput
      hideBorder
      textRight
      variant="secondary"
      className="!text-left"
      units={discountTypes}
      value={newDiscountValue}
      maxValue={isPercent ? MAX_PERCENT : amount}
      disabled={!permission?.billing?.discount || amount <= 0}
      onUnitChange={handleUnitChange}
      currentUnit={discountTypes.find((type) => type?.value === (discount?.type || 'amount'))}
      onBlur={handleDiscountChange}
    />
  );
};

export default Discount;
